'use client';

import type { AlertingService } from '@inato/browser-tracking';
import { UnexpectedError } from '@pages/UnexpectedError/UnexpectedError';
import { DateTime } from 'luxon';
import React from 'react';

import type { PersistentStorageService } from '../application/PersistentStorageService';
import type { PlatformService } from '../application/platform/PlatformService';

interface Props {
  alertingService: AlertingService;
  persistentStorageService: PersistentStorageService;
  platformService: PlatformService;
}

interface State {
  hasError: boolean;
}

const RELOADED_PAGE_BECAUSE_OF_CHUNK_LOAD_ERROR_AT =
  'reloadedPageBecauseOfChunkLoadErrorAt';

const CHUNK_FAILED_MESSAGE_REGEX = /Loading chunk [\d]+ failed/;
const NBR_OF_SECONDS_TO_CONSIDER_LAST_RELOAD_IS_LINKED_TO_SAME_ISSUE = 5;

export class ErrorBoundary extends React.Component<
  React.PropsWithChildren<Props>,
  State
> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (CHUNK_FAILED_MESSAGE_REGEX.test(error.message)) {
      const storedReloadedPageBecauseOfChunkLoadErrorAt =
        this.props.persistentStorageService.getItem(
          RELOADED_PAGE_BECAUSE_OF_CHUNK_LOAD_ERROR_AT,
        );
      const reloadedPageBecauseOfChunkLoadErrorAt =
        storedReloadedPageBecauseOfChunkLoadErrorAt
          ? DateTime.fromISO(storedReloadedPageBecauseOfChunkLoadErrorAt)
          : DateTime.utc(2000);
      const elapsedSinceLastReload = reloadedPageBecauseOfChunkLoadErrorAt
        .diffNow('seconds')
        .negate();
      if (
        elapsedSinceLastReload.seconds >=
        NBR_OF_SECONDS_TO_CONSIDER_LAST_RELOAD_IS_LINKED_TO_SAME_ISSUE
      ) {
        this.props.persistentStorageService.setItem(
          RELOADED_PAGE_BECAUSE_OF_CHUNK_LOAD_ERROR_AT,
          DateTime.utc().toISO(),
        );
        this.props.platformService.reload();
        return;
      }
      this.props.alertingService.reportError(error, {
        ...errorInfo,
        source: 'App/ErrorBoundary',
        elapsedSinceLastReload,
      });
      return;
    }

    const queryParamsAsTags = Object.fromEntries(
      new URLSearchParams(window.location.search).entries(),
    );
    this.props.alertingService.reportError(
      error,
      { ...errorInfo, source: 'App/ErrorBoundary' },
      queryParamsAsTags,
    );
  }

  override render() {
    if (this.state.hasError) {
      return <UnexpectedError />;
    }
    return this.props.children;
  }
}
